import { type AcceptStateDeliveryNote } from '~/modules/delivery-notes/list/constants';

import { fromEntries, isPlainObject, keys, map, pipe } from '@vestigas/utils';
import { log } from '~/utils';

import {
  type AcceptRole,
  type AcceptRoleCore,
  type BillingState,
  type DeliveryNoteAdminEnhanced,
  type DeliveryNoteAdminLineItemEnhanced,
  type DeliveryNoteAdminWithRelatedData,
  type DeliveryNoteEvent,
} from './types';
import {
  getAssetChainVersion,
  getAttachments,
  getCalculatedAcceptState,
  getCalculatedBillingState,
  // getDeliveryNoteHistory,
  getEvents,
  mapDeliveryNoteData,
  mapLineItemsData,
} from './utils';

/**
 * Maps the raw delivery note data from the API to a format we can use in the frontend.
 * @param data - The raw data from the API
 * @returns The mapped delivery note data
 */
export const selectDeliveryNoteData = (
  data: DeliveryNoteAdminWithRelatedData,
) => {
  let mappedDeliveryNoteData: DeliveryNoteAdminEnhanced | undefined;
  try {
    mappedDeliveryNoteData = mapDeliveryNoteData({
      assetChains: data?.assetChains,
      costCenters: data?.bulkCostCenters,
      customFields: data?.bulkCustomFields,
      deliveryNoteAsset: data?.deliveryNote,
      sites: data?.bulkSites,
      users: data?.bulkUsers,
    });
  } catch (error) {
    log.error({
      message: 'Error mapping raw delivery note data',
      error,
    });

    throw error;
  }

  let attachments;
  try {
    attachments = getAttachments(data.assetChains);
  } catch (error) {
    log.error({
      message: 'Error getting attachments from asset chains',
      error,
    });

    throw error;
  }

  // Process the line items using the already preprocessed delivery note and attachments data
  let mappedLineItemsData: DeliveryNoteAdminLineItemEnhanced[] = [];
  try {
    mappedLineItemsData = mapLineItemsData(
      data?.deliveryNote?.assetState?.body?.transaction?.logisticsPackage?.[0]
        ?.lineItem,
      mappedDeliveryNoteData,
      attachments,
      data?.bulkInvoices,
    );
  } catch (error) {
    log.error({
      message: 'Error mapping delivery note line items data',
      error,
    });

    throw error;
  }

  let acceptStateCalculated: Partial<
    Record<AcceptRoleCore, AcceptStateDeliveryNote>
  > = {};
  try {
    if (isPlainObject(data?.deliveryNote?.acceptStates?.acceptStatus)) {
      acceptStateCalculated = pipe(
        data.deliveryNote.acceptStates.acceptStatus,
        keys,
        map((role: AcceptRole) => {
          const lineItemAcceptStates = map(
            mappedLineItemsData ?? [],
            (lineItem) => lineItem.acceptState?.[role]?.compositeAcceptState,
          );

          return [role, getCalculatedAcceptState(lineItemAcceptStates)];
        }),
        fromEntries,
      );
    } else {
      acceptStateCalculated = {};
    }
  } catch (error) {
    log.error({
      message: 'Error calculating delivery note accept state',
      error,
    });

    throw error;
  }

  let billingStateCalculated: BillingState = 'unbilled';
  try {
    billingStateCalculated = getCalculatedBillingState(mappedLineItemsData);
  } catch (error) {
    log.error({
      message: 'Error calculating delivery note billing state',
      error,
    });

    throw error;
  }

  // Process the asset chains in a similar way to the full delivery note (asset main) to get the same data structure
  const mappedAssetChainsData = data?.assetChains?.map((assetChain) => {
    const assetChainEnhanced = mapDeliveryNoteData({
      assetChains: data?.assetChains,
      costCenters: data?.bulkCostCenters,
      customFields: data?.bulkCustomFields,
      deliveryNoteAsset: assetChain,
      sites: data?.bulkSites,
      users: data?.bulkUsers,
    });

    // Process the line items using the already preprocessed asset chain and attachments data
    let mappedLineItemsDataAssetChain: DeliveryNoteAdminLineItemEnhanced[] = [];
    try {
      mappedLineItemsDataAssetChain = mapLineItemsData(
        assetChain?.body?.transaction?.logisticsPackage?.[0]?.lineItem,
        assetChainEnhanced,
        attachments,
        data?.bulkInvoices,
      );
    } catch (error) {
      log.error({
        message: 'Error mapping asset chain line items data',
        error,
      });

      throw error;
    }

    const assetVersion = getAssetChainVersion(data?.assetChains, assetChain.id);

    const lineItemAcceptStates = assetChainEnhanced?.acceptedItems?.map(
      ({ compositeAcceptState }) => compositeAcceptState,
    );
    const calculatedAcceptState =
      getCalculatedAcceptState(lineItemAcceptStates);

    return {
      ...assetChainEnhanced,
      assetChainContext: {
        // Additional properties that an asset chain has, but the asset main does not
        assetId: assetChain.assetId,
        assetVersion,
        attachments: (attachments ?? []).filter(
          ({ chainId }) => chainId === assetChain.id,
        ),
        context: assetChain.body?.context,
        idPrevItem: assetChain.idPrevItem,
        meta: assetChain.meta,
        signatureRole: assetChain.signatureRole,
      },
      calculatedAcceptState,
      createdBy: assetChain.createdBy,
      lineItems: mappedLineItemsDataAssetChain,
    };
  });

  let events: DeliveryNoteEvent[] = [];
  try {
    events = getEvents({
      assetChainsEnhanced: mappedAssetChainsData,
      lineItems: mappedLineItemsData,
      users: data?.bulkUsers,
    });
  } catch (error) {
    log.error({
      message: 'Error getting delivery note events',
      error,
    });

    throw error;
  }

  // let history = [];
  // try {
  //   // This is very unreliable WIP code!
  //   history = getDeliveryNoteHistory(mappedDeliveryNoteData, mappedAssetChainsData);
  // } catch (error) {
  //   log.error({
  //     message: 'Error getting delivery note history',
  //     error,
  //   });

  //   throw error;
  // }
  // console.log('history', history);

  return {
    ...(mappedDeliveryNoteData ?? {}),
    acceptStateCalculated,
    attachments,
    billingStateCalculated,
    events,
    // history,
    lineItems: mappedLineItemsData,
  };
};
